import React from 'react';
import styled from 'styled-components';
import { useInView } from 'react-intersection-observer';

import { Button } from '@lt/components';
import {
  ArrowRightLong,
  FlameDiscountBlue,
  SuccessMarkDone,
} from '@lt/components/icons-react/userflow';

import { i18n } from '@lt/localization';
import { formatCurrency } from '../../../../../utils/stringUtils';

import { DISCOUNT_BONUS } from '../../../store/constants/discount';

import { NBSP } from '../../../../../constants/common';
import {
  DiscountSuccessContentAnimation,
  DiscountSuccessIconAnimation,
  DiscountSuccessTitleAnimation,
} from '../animation';

interface AppliedCashbackOrPromoProps {
  appliedType?: string;
  discountPrice?: number;
  fullPrice?: number;
  isMobile?: boolean;
  setDiscountButton: JSX.Element;
  onCancelBonus: () => void;
}

const AppliedCashbackOrPromo = ({
  appliedType,
  discountPrice,
  fullPrice,
  isMobile,
  setDiscountButton,
  onCancelBonus,
}: AppliedCashbackOrPromoProps) => {
  const { ref, inView } = useInView({ triggerOnce: true });

  const label =
    appliedType === DISCOUNT_BONUS
      ? i18n.t('checkout.discount.cashbackUsed')
      : i18n.t('checkout.discount.promocodeActivated');

  return (
    <AppliedMessageWrapper ref={ref}>
      {inView ? (
        <>
          <StyledSuccessMarkIcon isMobile={isMobile} />
          <AppliedMessageTitle>{label}</AppliedMessageTitle>
          <AppliedMessageContent>
            {discountPrice ? (
              <AppliedMessageCount>
                {i18n.t('checkout.discount.saved')}{' '}
                {formatCurrency(discountPrice)}
              </AppliedMessageCount>
            ) : null}
            {fullPrice ? (
              <AppliedMessageDifference>
                {discountPrice ? (
                  <OldPrice>
                    {formatCurrency(fullPrice + discountPrice)}
                  </OldPrice>
                ) : null}
                <StyledArrowRightIcon />
                {formatCurrency(fullPrice)}
              </AppliedMessageDifference>
            ) : null}
            {setDiscountButton}
            {appliedType === DISCOUNT_BONUS && (
              <CancelCashback isMobile={isMobile} onClick={onCancelBonus}>
                <StyledFlameDiscountIcon />
                {i18n.t('checkout.discount.useInstead', { nbsp: NBSP })}
              </CancelCashback>
            )}
          </AppliedMessageContent>
        </>
      ) : null}
    </AppliedMessageWrapper>
  );
};

const AppliedMessageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  padding: 16px 12px;
  background: ${({ theme }) => theme.colors.white};
`;

const AppliedMessageTitle = styled.div`
  font-size: 18px;
  line-height: 22px;
  font-weight: 700;
  margin-bottom: 8px;

  ${DiscountSuccessTitleAnimation}
`;

const AppliedMessageCount = styled.div`
  font-size: 14px;
  line-height: 16px;
  margin-bottom: 4px;
`;

const AppliedMessageDifference = styled.div`
  display: flex;
  align-items: center;
  font-size: 12px;
  line-height: 16px;
  margin-bottom: 16px;
`;

const StyledArrowRightIcon = styled(ArrowRightLong)`
  height: 6px;
  width: 30px;
  margin: 0 4px;
  color: #c2c5cc;
`;

const OldPrice = styled.span`
  color: #c2c5cc;
`;

const StyledFlameDiscountIcon = styled(FlameDiscountBlue)`
  position: relative;
  top: 5px;
  margin-top: -5px;
  margin-right: 4px;
  width: 20px;
`;

const CancelCashback = styled(Button)<{ isMobile?: boolean }>`
  height: auto;
  width: 100%;
  border: none;
  padding: 0;
  margin-top: ${({ isMobile }) => (isMobile ? 12 : 36)}px;
  overflow: auto;
  white-space: normal;

  &:hover svg path {
    stroke: #179fe3;
  }
`;

const AppliedMessageContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;

  ${DiscountSuccessContentAnimation}
`;

const StyledSuccessMarkIcon = styled(SuccessMarkDone)<{ isMobile?: boolean }>`
  width: 48px;
  margin-bottom: ${({ isMobile }) => (isMobile ? 8 : 16)}px;

  ${DiscountSuccessIconAnimation}
`;

export default AppliedCashbackOrPromo;
